/**
 * # FilterChip
 *
 * A custom filter chip component built with React and React Select.
 *
 * ## Props
 *
 * - `value` (object, required): The selected value of the filter chip.
 * - `onChange` (function, required): The callback function called when the value of the filter chip changes.
 * - `label` (string): The label of the filter chip.
 * - `color` (string): The color of the filter chip. Must be one of type ChipColor
 * - `options` (array): The options for the filter chip.
 * - `disabled` (boolean): Specifies whether the filter chip is disabled.
 * - `hasFocus` (boolean): Specifies whether the filter chip has focus.
 * - `hasHover` (boolean): Specifies whether the filter chip is being hovered.
 * - `role` (string): The role of the filter chip.
 * - `className` (string): The CSS class name for the filter chip.
 *
 * ## Usage
 *
 * ```jsx
 * import FilterChip from './FilterChip.react';
 *
 * const options = [
 *   { value: 'option1', label: 'Option 1' },
 *   { value: 'option2', label: 'Option 2' },
 *   { value: 'option3', label: 'Option 3' },
 * ];
 *
 * const MyComponent = () => {
 *   const [selectedValue, setSelectedValue] = useState(null);
 *
 *   const handleChange = (value) => {
 *     setSelectedValue(value);
 *   };
 *
 *   return (
 *     <FilterChip
 *       value={selectedValue}
 *       onChange={handleChange}
 *       label="Filter"
 *       color="brand"
 *       options={options}
 *       disabled={false}
 *       hasFocus={false}
 *       hasHover={false}
 *       role="button"
 *       className="my-filter-chip"
 *     />
 *   );
 * };
 * ```
 */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Select, {components} from 'react-select';

import Chip from '@albert-io/atomic/atoms/Chip/Chip.react';
import type {ChipColor} from '@albert-io/atomic/atoms/Chip/Chip.react';
import Text from '@albert-io/atomic/atoms/Text/Text.react';
import WithHoverAndFocus from '@albert-io/atomic/helpers/WithHoverAndFocus/WithHoverAndFocus.react';

import './filter-chip.scss';

interface FilterChipOption {
  value: string;
  label: string;
}

const {ValueContainer} = components;

const customStyles = {
  control: () => ({
    display: 'flex',
    cursor: 'pointer',
    width: '100%'
  }),
  dropdownIndicator: () => ({
    color: 'inherit',
    display: 'flex'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  option: (provided: Record<string, unknown>, state: {isSelected: boolean}) => ({
    ...provided,
    borderBottom: '1px solid #dfe0e0',
    color: state.isSelected ? '#399fdf' : 'rgba(0, 0, 0, 0.7)', // selected: $blue-400
    cursor: 'pointer',
    fontWeight: 700,
    padding: 16,
    background: 'transparent'
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    left: 0,
    width: 'auto',
    minWidth: '100%'
  }),
  valueContainer: () => ({
    flexWrap: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flex: 1
  }),
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})
};

interface CustomControlProps {
  children?: React.ReactNode;
  innerRef?: React.Ref<unknown>;
  innerProps?: Record<string, unknown>;
  selectProps?: Record<string, unknown>;
  [key: string]: unknown;
}

const CustomControl = ({
  children,
  innerRef,
  innerProps,
  selectProps,
  ...props
}: CustomControlProps) => {
  return (
    <WithHoverAndFocus>
      {({hasFocus, hasHover, ...withHoverAndFocusHandlers}) => (
        <Chip
          ref={innerRef}
          hasFocus={hasFocus}
          hasHover={hasHover}
          style={{cursor: 'pointer'}}
          {...innerProps}
          {...withHoverAndFocusHandlers}
          {...selectProps}
          {...props}
        >
          {children}
        </Chip>
      )}
    </WithHoverAndFocus>
  );
};

CustomControl.propTypes = {
  children: PropTypes.node,
  innerRef: PropTypes.object,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object
};

interface CustomValueContainerSelectProps {
  label?: string;
  value?: FilterChipOption;
  [key: string]: unknown;
}

interface CustomValueContainerProps {
  children?: React.ReactNode;
  selectProps: CustomValueContainerSelectProps;
  [key: string]: unknown;
}

const CustomValueContainer = ({children, selectProps, ...props}: CustomValueContainerProps) => {
  const selectInput = React.Children.toArray(children).find((input) => {
    const inputType = (input as React.ReactElement).type;
    const typeName = typeof inputType === 'function' ? inputType.name : undefined;
    return typeName === 'Input' || typeName === 'DummyInput';
  });
  const {label, value, ...restSelectProps} = selectProps;
  return (
    <ValueContainer {...restSelectProps} {...props}>
      <Text bold size='s' color='inherit' className='mr-x1'>
        {label}:
      </Text>
      <Text size='s' color='inherit' className='overflow-hidden text-ellipsis'>
        {value?.value || 'Any'}
      </Text>
      {selectInput}
    </ValueContainer>
  );
};

CustomValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.object
};

interface FilterChipProps {
  value: FilterChipOption;
  onChange: (value: FilterChipOption) => void;
  label?: string;
  color?: ChipColor;
  options?: FilterChipOption[];
  disabled?: boolean;
  hasFocus?: boolean;
  hasHover?: boolean;
  role?: string;
  className?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

const FilterChip = ({
  value,
  onChange,
  label = 'filterLabel',
  color,
  options = [],
  disabled,
  className,
  ...rest
}: FilterChipProps) => {
  return (
    <Select
      className={classnames('m-filterchip__select', className)}
      styles={customStyles}
      aria-label={label}
      label={label}
      color={color}
      isSearchable={false}
      disabled={disabled}
      onChange={onChange}
      value={value}
      data-testid='m-filterchip__select'
      options={options}
      components={{
        ValueContainer: CustomValueContainer,
        Control: CustomControl
      }}
      {...rest}
    />
  );
};

FilterChip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
  hasFocus: PropTypes.bool,
  hasHover: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object.isRequired,
  role: PropTypes.string
};

export default FilterChip;
